import { useEffect, useMemo, useState } from "react";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, MessageCircle, Plus, Trash2 } from "lucide-react";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { CardPracaHoje } from "@/components/mercado/card-praca-hoje";
import { StatusChip } from "@/components/mercado/status-chip";
import { useToast } from "@/hooks/use-toast";
import { useResolverProdutor } from "@/hooks/use-cargas";
import {
  useCarteira, useCargasEmFormacao, useEditarRomaneio, useRegistrarRomaneio, useRomaneio,
} from "@/hooks/use-romaneios";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import { fmtMoedaBR, fmtKgBR, fmtDataBR } from "@/lib/formato-mercado";
import { diaCivilNaFazenda, somarDiasCivis } from "@shared/data-fazenda";
import { PRODUTOS, chaveProduto } from "@shared/mercado-produtos";
import {
  ATALHOS_VENCIMENTO_ITEM, DIAS_VENCIMENTO_PADRAO_ITEM,
  type ItemRegistrado, type ProdutorCarteiraDTO,
} from "@shared/mercado-comprador";

/**
 * REGISTRAR ROMANEIO — passar para o Rizoma o que ele anotou no bloco.
 *
 * ⚠️ SEM CARGA, e é a mudança do ciclo (073): ele registra na fazenda, na beira
 * do caminhão, e decide a carga depois. O campo "Carga" existe e é opcional —
 * quando ele já sabe em qual caminhão aquilo sobe, ganha um passo; quando não
 * sabe, o romaneio nasce avulso e aparece em "Sem carga".
 *
 * ⚠️ O TOTAL É DO SERVIDOR. A prévia daqui é conferência visual do que foi
 * digitado; quem calcula o `valor` gravado é `valorDoRegistro`, e é o número do
 * servidor que o produtor vai conferir no link. Duas contas do mesmo total
 * divergem no primeiro centavo de arredondamento.
 *
 * ⚠️ A cotação da praça entra como REFERÊNCIA e NUNCA preenche o preço: o preço
 * é o que foi combinado com aquele produtor, e preencher o campo com a praça
 * transformaria uma referência num valor que ninguém acordou.
 *
 * A MESMA tela edita: com `refRomaneio`, carrega o romaneio e salva com PATCH.
 * Dois formulários para os mesmos oito campos divergiriam no primeiro campo novo.
 */

/** Uma linha do formulário — `kg`/`preco` ficam como TEXTO até o envio. */
interface LinhaForm {
  produto: string;
  kg: string;
  preco: string;
}

const LINHA_VAZIA: LinhaForm = { produto: chaveProduto(PRODUTOS[0]), kg: "", preco: "" };

/** "Não incluir agora" — o valor do select de carga quando ele decide depois. */
const SEM_CARGA = "nenhuma";

export default function CompradorRomaneioForm({ refRomaneio }: { refRomaneio?: string }) {
  const { toast } = useToast();
  const { voltar, substituir } = useNavegacao();
  const editando = !!refRomaneio;

  const detalhe = useRomaneio(refRomaneio);
  const { data: carteira } = useCarteira(true);
  const { data: cargas } = useCargasEmFormacao(!editando);
  const registrar = useRegistrarRomaneio();
  const editar = useEditarRomaneio(refRomaneio);

  const hoje = diaCivilNaFazenda(new Date());

  const [nome, setNome] = useState("");
  const [contatoId, setContatoId] = useState<string | null>(null);
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");
  const [dataDocumento, setDataDocumento] = useState(hoje);
  const [numeroBloco, setNumeroBloco] = useState("");
  const [linhas, setLinhas] = useState<LinhaForm[]>([{ ...LINHA_VAZIA }]);
  const [dias, setDias] = useState<number | null>(DIAS_VENCIMENTO_PADRAO_ITEM);
  const [dataLivre, setDataLivre] = useState("");
  const [cargaId, setCargaId] = useState(SEM_CARGA);
  const [duplicata, setDuplicata] = useState<ProdutorCarteiraDTO[] | null>(null);

  // Modo edição: o formulário nasce com o que já está gravado. Só uma vez — um
  // `useEffect` sem guarda apagaria o que ele está digitando a cada refetch.
  const [carregado, setCarregado] = useState(false);
  useEffect(() => {
    if (!editando || carregado || !detalhe.data) return;
    const d = detalhe.data;
    setNome(d.produtor.nome);
    setContatoId(d.produtor.contatoId);
    setDataDocumento(d.data);
    setNumeroBloco(d.numero !== "registro" ? d.numero : "");
    setLinhas(d.itens.map(i => {
      const produto = PRODUTOS.find(p => p.variedade === i.variedade && p.qualidade === i.qualidade);
      return {
        produto: produto ? chaveProduto(produto) : chaveProduto(PRODUTOS[0]),
        kg: String(i.kg).replace(".", ","),
        preco: String(i.precoKg).replace(".", ","),
      };
    }));
    // O prazo já combinado vira data livre: recontar "15 dias" a partir do
    // carregamento poderia MUDAR um vencimento que as duas partes já acertaram.
    setDias(null);
    setDataLivre(d.vencimento ?? "");
    setCarregado(true);
  }, [editando, carregado, detalhe.data]);

  // Autocomplete: só quando ele está digitando um nome NOVO.
  const { data: resolucao } = useResolverProdutor(contatoId ? "" : nome);

  const itens: ItemRegistrado[] = useMemo(
    () => linhas
      .filter(l => l.kg && l.preco)
      .map(l => {
        const produto = PRODUTOS.find(p => chaveProduto(p) === l.produto)!;
        return {
          variedade: produto.variedade,
          qualidade: produto.qualidade,
          kg: Number(l.kg.replace(",", ".")),
          precoKg: Number(l.preco.replace(",", ".")),
        };
      })
      .filter(i => Number.isFinite(i.kg) && i.kg > 0 && Number.isFinite(i.precoKg)),
    [linhas],
  );

  const totalKg = itens.reduce((a, i) => a + i.kg, 0);
  const totalValor = itens.reduce((a, i) => a + i.kg * i.precoKg, 0);

  // ⚠️ Contado do CARREGAMENTO, não de hoje: o prazo que os dois combinaram
  // corre do dia em que a fruta saiu da fazenda, e registrar dois dias depois
  // não pode empurrar o vencimento.
  const vencimento = dias != null && dataDocumento
    ? somarDiasCivis(dataDocumento, dias)
    : (dataLivre || null);

  const primeiroNome = (nome.trim() || "o produtor").split(/\s+/)[0];
  const salvando = registrar.isPending || editar.isPending;
  const podeSalvar = !!nome.trim() && itens.length > 0 && !!dataDocumento && !salvando;

  function corpo(produtor: { contatoId?: string; nome?: string; telefone?: string | null; cidade?: string | null; criarNovo?: boolean }) {
    return {
      produtor,
      dataDocumento,
      numeroBloco: numeroBloco.trim() || null,
      itens,
      vencimento,
      cargaId: cargaId !== SEM_CARGA ? cargaId : null,
    };
  }

  function salvar(opcoes: { criarNovo?: boolean; enviar?: boolean; contato?: string } = {}) {
    if (itens.length === 0) {
      toast({
        title: "Falta o que ele carregou",
        description: "Informe pelo menos uma linha com kg e preço.",
        variant: "destructive",
      });
      return;
    }

    if (editando) {
      editar.mutate(
        { dataDocumento, numeroBloco: numeroBloco.trim() || null, itens, vencimento },
        {
          // Salvar EDIÇÃO = voltar: ele veio do painel, e é para lá que volta.
          onSuccess: () => voltar("/mercado/romaneios"),
          onError: (e: Error) => toast({ title: "Não consegui salvar", description: e.message, variant: "destructive" }),
        },
      );
      return;
    }

    const id = opcoes.contato ?? contatoId;
    registrar.mutate(
      corpo(id ? { contatoId: id } : {
        nome: nome.trim(),
        telefone: telefone.trim() || null,
        cidade: cidade.trim() || null,
        criarNovo: opcoes.criarNovo,
      }),
      {
        onSuccess: async (r) => {
          // A duplicata é PERGUNTA: nada foi gravado, e a tela pergunta antes de
          // criar o segundo cadastro do mesmo produtor.
          if (!r.ok) { setDuplicata(r.parecidos); return; }
          if (opcoes.enviar) await abrirWhatsApp(r.romaneio.ref);
          // Salvar CRIAÇÃO = `substituir`: o formulário troca de URL pelo
          // romaneio recém-criado sem deixar o /novo no histórico — "Voltar"
          // leva à lista, não a um formulário em branco.
          substituir(`/mercado/romaneios/${encodeURIComponent(r.romaneio.ref)}`);
        },
        onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
      },
    );
  }

  /**
   * Abre o WhatsApp do produtor com o texto e o link `/p/`.
   *
   * ⚠️ A PLATAFORMA NÃO MANDA MENSAGEM: o servidor devolve texto e link, e quem
   * toca em enviar é o corretor. O evento `LINK_ENVIADO` registra a INTENÇÃO —
   * não há como saber se a mensagem saiu.
   */
  async function abrirWhatsApp(ref: string) {
    try {
      const m = await fetchJson<{ texto: string; link: string | null }>(
        `/api/mercado/comprador/romaneios/${encodeURIComponent(ref)}/mensagem`,
      );
      await apiRequest("POST", `/api/mercado/comprador/romaneios/${encodeURIComponent(ref)}/link-enviado`)
        // Gravar o evento é HISTÓRICO; travar o envio porque ele não gravou seria
        // impedir o ato por causa do registro dele.
        .catch(() => undefined);
      if (m.link) window.open(m.link, "_blank", "noopener");
      else toast({ title: "Sem WhatsApp cadastrado", description: "O romaneio foi salvo. Cadastre o telefone do produtor para enviar o link." });
    } catch (e) {
      // O romaneio JÁ foi gravado — falhar aqui não pode parecer que ele se perdeu.
      toast({
        title: "Romaneio salvo, mas não consegui abrir o WhatsApp",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  if (editando && detalhe.isLoading) {
    return <CompradorShell><PageSkeleton variante="painel" /></CompradorShell>;
  }
  if (editando && (detalhe.isError || !detalhe.data)) {
    return (
      <CompradorShell>
        <div className="p-6 text-center space-y-3">
          <p className="font-bold">Romaneio não encontrado.</p>
          <Button variant="outline" onClick={() => voltar("/mercado/romaneios")}>Voltar</Button>
        </div>
      </CompradorShell>
    );
  }

  const emFormacao = cargas ?? [];

  return (
    <CompradorShell>
      {/* Folha estreita: é um formulário de uma coisa só, e largura cheia faria
          os campos atravessarem a tela inteira no desktop. */}
      <div className="mx-auto w-full max-w-[640px] space-y-4">
        <Button variant="ghost" size="sm" onClick={() => voltar("/mercado/romaneios")} className="-ml-2" data-testid="button-voltar">
          <ArrowLeft className="h-4 w-4 mr-1" />Romaneios
        </Button>

        <div>
          <h1 className="text-xl md:text-2xl font-bold">{editando ? "Editar romaneio" : "Registrar romaneio"}</h1>
          <p className="text-sm text-muted-foreground">
            Passe para o Rizoma o romaneio que você anotou no bloco. Leva um minuto.
          </p>
        </div>

        {/* ── Produtor ── */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="produtor-nome">Produtor</Label>

            {/* Os chips da carteira são o caminho de um toque: no dia a dia ele
                compra dos mesmos oito produtores, e digitar o nome inteiro toda
                vez, no celular, no pátio, é o que faz voltar para o caderno. */}
            {!editando && (carteira ?? []).length > 0 && (
              <div className="flex gap-1.5 flex-wrap" data-testid="chips-carteira">
                {(carteira ?? []).slice(0, 8).map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setContatoId(p.id); setNome(p.nome); }}
                    aria-pressed={contatoId === p.id}
                    data-testid={`chip-produtor-${p.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover-elevate ${
                      contatoId === p.id ? "border-primary bg-primary/10 text-primary" : "bg-card"
                    }`}
                  >
                    {p.nome}
                    {p.noRizoma && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-1))]" aria-label="no Rizoma" />}
                  </button>
                ))}
              </div>
            )}

            <Input
              id="produtor-nome" value={nome} autoComplete="off" disabled={editando}
              onChange={e => { setNome(e.target.value); setContatoId(null); }}
              placeholder="Nome ou apelido" data-testid="input-produtor-nome"
            />

            <p className="text-xs text-muted-foreground">
              Produtores do Rizoma mandam o romaneio pelo sistema. Registre aqui só se ele ainda não enviou.
            </p>

            {/* O autocomplete evita o segundo cadastro ANTES do 409 — é mais
                barato reconhecer do que responder a um diálogo. */}
            {!editando && !contatoId && (resolucao?.parecidos.length || resolucao?.exato) && (
              <ul className="rounded-lg border divide-y" data-testid="lista-sugestoes-produtor">
                {[resolucao.exato, ...resolucao.parecidos].filter(Boolean).slice(0, 4).map(p => (
                  <li key={p!.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover-elevate flex items-center justify-between gap-2"
                      onClick={() => { setContatoId(p!.id); setNome(p!.nome); }}
                      data-testid={`sugestao-${p!.id}`}
                    >
                      <span className="truncate">{p!.nome}{p!.cidade ? ` · ${p!.cidade}` : ""}</span>
                      {p!.noRizoma && <StatusChip kind="pago">no Rizoma</StatusChip>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!editando && !contatoId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="produtor-telefone">WhatsApp</Label>
                <Input
                  id="produtor-telefone" inputMode="tel" value={telefone}
                  onChange={e => setTelefone(e.target.value)} placeholder="(74) 99999-8888"
                  data-testid="input-produtor-telefone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="produtor-cidade">Cidade</Label>
                <Input id="produtor-cidade" value={cidade} onChange={e => setCidade(e.target.value)} />
              </div>
            </div>
          )}
        </section>

        {/* ── Data, bloco ── */}
        <section className="rounded-lg border bg-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="data-documento">Data do carregamento</Label>
            <Input
              id="data-documento" type="date" value={dataDocumento}
              onChange={e => setDataDocumento(e.target.value)}
              data-testid="input-data-documento"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="numero-bloco">
              Nº no seu bloco <span className="text-muted-foreground font-normal">opcional</span>
            </Label>
            <Input
              id="numero-bloco" value={numeroBloco} inputMode="numeric"
              onChange={e => setNumeroBloco(e.target.value)} placeholder="2215"
              className="font-mono" data-testid="input-numero-bloco"
            />
          </div>
        </section>

        {/* ── O que carregou ── */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <h2 className="font-semibold">O que carregou</h2>

          <ul className="space-y-3">
            {linhas.map((linha, i) => (
              <li key={i} className="grid grid-cols-[1fr_auto] gap-2 items-end" data-testid={`linha-item-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`produto-${i}`}>Classificação</Label>
                    <Select
                      value={linha.produto}
                      onValueChange={v => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, produto: v } : l))}
                    >
                      <SelectTrigger id={`produto-${i}`} data-testid={`select-produto-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRODUTOS.map(p => (
                          <SelectItem key={chaveProduto(p)} value={chaveProduto(p)}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`kg-${i}`}>kg</Label>
                    <Input
                      id={`kg-${i}`} inputMode="decimal" value={linha.kg}
                      onChange={e => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, kg: e.target.value } : l))}
                      data-testid={`input-kg-${i}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`preco-${i}`}>R$/kg</Label>
                    <Input
                      id={`preco-${i}`} inputMode="decimal" value={linha.preco}
                      onChange={e => setLinhas(ls => ls.map((l, j) => j === i ? { ...l, preco: e.target.value } : l))}
                      data-testid={`input-preco-${i}`}
                    />
                  </div>
                </div>
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => setLinhas(ls => ls.length === 1 ? [{ ...LINHA_VAZIA }] : ls.filter((_, j) => j !== i))}
                  aria-label="Remover linha"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>

          <Button
            type="button" variant="outline" size="sm"
            onClick={() => setLinhas(ls => ls.concat([{ ...LINHA_VAZIA }]))}
            data-testid="button-mais-classificacao"
          >
            <Plus className="h-4 w-4 mr-1" />Classificação
          </Button>

          {itens.length > 0 && (
            <p className="text-sm tabular-nums pt-2 border-t">
              Total: <b>{fmtKgBR(totalKg)}</b> — <b>{fmtMoedaBR(totalValor)}</b>
            </p>
          )}

          {/* REFERÊNCIA, nunca preenchimento — ver o cabeçalho do arquivo. A
              praça da DATA DO DOCUMENTO, não a de hoje. */}
          <CardPracaHoje dataVenda={dataDocumento} />
        </section>

        {/* ── Vencimento e carga ── */}
        <section className="rounded-lg border bg-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Vencimento</Label>
            <div className="flex gap-2 flex-wrap">
              {ATALHOS_VENCIMENTO_ITEM.map(d => (
                <Button
                  key={d} type="button" size="sm"
                  variant={dias === d ? "default" : "outline"}
                  onClick={() => { setDias(d); setDataLivre(""); }}
                  disabled={!dataDocumento}
                  data-testid={`chip-vencimento-${d}`}
                >
                  {d} dias
                </Button>
              ))}
            </div>
            <Input
              type="date" value={dataLivre}
              onChange={e => { setDataLivre(e.target.value); setDias(null); }}
              data-testid="input-vencimento-data"
            />
            <p className="text-xs text-muted-foreground">
              {vencimento
                ? <>Vence em <b>{fmtDataBR(vencimento)}</b>{dias != null && " · contado do carregamento"}</>
                : "Sem vencimento — fica \"a definir\"."}
            </p>
          </div>

          {!editando && (
            <div className="space-y-2">
              <Label htmlFor="carga-do-romaneio">
                Carga <span className="text-muted-foreground font-normal">opcional</span>
              </Label>
              <Select value={cargaId} onValueChange={setCargaId}>
                <SelectTrigger id="carga-do-romaneio" data-testid="select-carga-do-romaneio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_CARGA}>Não incluir agora</SelectItem>
                  {emFormacao.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.identificador} · em formação
                      {c.faltam != null && c.faltam > 0 && ` · faltam ${fmtKgBR(c.faltam)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Você pode incluir depois, pela lista de romaneios.
              </p>
            </div>
          )}
        </section>

        {/* ── Prévia ── */}
        {!editando && itens.length > 0 && (
          <details className="rounded-lg border bg-card p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Ver a mensagem que {primeiroNome} vai receber
            </summary>
            <pre className="whitespace-pre-wrap text-sm font-sans mt-3 text-muted-foreground">
              {previa({ nome: nome.trim() || "Produtor", itens, totalKg, totalValor, vencimento })}
            </pre>
            {/* A prévia espelha o texto do servidor na FORMA, não no byte: o
                texto de verdade é montado lá (`montarMensagemDoItem`), e o dia
                em que os dois divergirem é o dia em que a prévia mente. */}
            <p className="text-xs text-muted-foreground mt-2">
              O texto final e o link saem do servidor ao enviar.
            </p>
          </details>
        )}

        <div className="flex flex-wrap gap-2 pb-4">
          <Button variant="outline" onClick={() => voltar("/mercado/romaneios")} data-testid="button-cancelar">
            Cancelar
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button onClick={() => salvar()} disabled={!podeSalvar} data-testid="button-salvar-romaneio">
              {salvando ? "Salvando…" : "Salvar"}
            </Button>
            {!editando && (
              <Button
                onClick={() => salvar({ enviar: true })}
                disabled={!podeSalvar}
                data-testid="button-salvar-e-enviar"
              >
                <MessageCircle className="h-4 w-4 mr-1.5" />Salvar e enviar ao {primeiroNome}
              </Button>
            )}
          </div>
        </div>
      </div>

      <DialogDuplicata
        parecidos={duplicata}
        nomeDigitado={nome.trim()}
        onEhEle={(id) => { setDuplicata(null); setContatoId(id); salvar({ contato: id }); }}
        onEhOutro={() => { setDuplicata(null); salvar({ criarNovo: true }); }}
        onFechar={() => setDuplicata(null)}
      />
    </CompradorShell>
  );
}

/**
 * O diálogo de DUAS opções — "É ele" / "É outro".
 *
 * Duas e não três: "cancelar" já é o X do diálogo, e uma terceira via ("criar
 * mesmo assim, mas me pergunte de novo") não existe no mundo. A pergunta é curta
 * porque acontece no meio de digitar um romaneio, na beira do caminhão.
 */
function DialogDuplicata({
  parecidos, nomeDigitado, onEhEle, onEhOutro, onFechar,
}: {
  parecidos: ProdutorCarteiraDTO[] | null;
  nomeDigitado: string;
  onEhEle: (id: string) => void;
  onEhOutro: () => void;
  onFechar: () => void;
}) {
  const primeiro = parecidos?.[0];
  return (
    <Dialog open={!!primeiro} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Já existe alguém parecido</DialogTitle>
          <DialogDescription>
            {primeiro && (
              <><b>{primeiro.nome}</b>{primeiro.cidade ? ` (${primeiro.cidade})` : ""} já está na sua carteira.
                Você digitou <b>{nomeDigitado}</b>. É ele?</>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={onEhOutro} data-testid="button-e-outro">
            É outro
          </Button>
          <Button className="flex-1" onClick={() => primeiro && onEhEle(primeiro.id)} data-testid="button-e-ele">
            É ele
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A prévia do que o produtor vai ler — ver o aviso no `details` que a usa. */
function previa(d: {
  nome: string;
  itens: ItemRegistrado[];
  totalKg: number;
  totalValor: number;
  vencimento: string | null;
}): string {
  const linhas = d.itens.map(i => {
    const rotulo = PRODUTOS.find(p => p.variedade === i.variedade && p.qualidade === i.qualidade)?.label
      ?? `${i.variedade} ${i.qualidade ?? ""}`.trim();
    return `• ${rotulo} — ${fmtKgBR(i.kg)} × ${fmtMoedaBR(i.precoKg)} — ${fmtMoedaBR(i.kg * i.precoKg)}`;
  });
  return [
    `${d.nome}`,
    "",
    ...linhas,
    "",
    `Total: ${fmtKgBR(d.totalKg)} — ${fmtMoedaBR(d.totalValor)}`,
    d.vencimento ? `Vencimento: ${fmtDataBR(d.vencimento)}` : "",
  ].filter(l => l !== undefined).join("\n").trim();
}
